/**
 * `applyRunEvent` — SSE 이벤트 하나를 상태에 접는 **순수 함수**.
 *
 * 구 구조에서는 이 변환이 Nitro(서버)에서 AI SDK UIMessage part 로 일어났다. 여기서는
 * 브라우저에서 일어나고, **순수 함수라 브라우저 없이 테스트된다** — 골든 입력은 백엔드가
 * 실제로 내보낸 프레임 스냅샷(`tests/fixtures/sse/*.json`)이다.
 *
 * 지켜야 하는 불변식 다섯(전부 F-0 특성화에서 나온 것):
 *  1. `gate` 가 오면 **`done` 이 오지 않는다** → 상태는 `awaiting_gate`. "끊겼다"로 읽으면
 *     유일한 비가역 조작(발주)이 사라지거나 영원히 대기한다.
 *  2. `error` 로 끝나면 `done` 이 없다 → `error` 상태로 종결(마스킹된 문구를 그대로 보여준다).
 *  3. `ping` 은 **무시하되 죽지 않는다**. 가짜 진행(text/tool)으로 바꾸지 않는다.
 *  4. **미지의 이벤트도 죽지 않는다** — 백엔드가 새 이벤트를 추가해도 화면이 멈추면 안 된다.
 *  5. `tool` 결과가 호출 없이 먼저 와도(재개 턴) **반쪽 카드가 되지 않는다**(orphan 방지).
 */
import type {
  AssistantMessage,
  AssistantPart,
  ChatMessage,
  RunEvent,
  RunState,
  TextPart,
  ToolPart,
} from './events'
import { labelFor } from './toolLabels'

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function record(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

/** 마지막 assistant 메시지(없으면 새로 연다) — 사용자 발화 뒤에 이어붙일 자리. */
function withAssistant(
  messages: ChatMessage[],
  mutate: (parts: AssistantPart[]) => AssistantPart[],
): ChatMessage[] {
  const last = messages[messages.length - 1]
  if (last && last.role === 'assistant') {
    const updated: AssistantMessage = { ...last, parts: mutate(last.parts) }
    return [...messages.slice(0, -1), updated]
  }
  const created: AssistantMessage = {
    role: 'assistant',
    id: `a-${messages.length}`,
    parts: mutate([]),
  }
  return [...messages, created]
}

/** 열려 있는 텍스트 블록에 델타를 붙인다(없으면 새 블록). 도구·게이트가 오면 블록은 닫힌다. */
function appendText(parts: AssistantPart[], delta: string): AssistantPart[] {
  const last = parts[parts.length - 1]
  if (last && last.kind === 'text') {
    const merged: TextPart = { ...last, text: last.text + delta }
    return [...parts.slice(0, -1), merged]
  }
  return [...parts, { kind: 'text', id: `t-${parts.length}`, text: delta }]
}

function upsertTool(parts: AssistantPart[], incoming: ToolPart): AssistantPart[] {
  const index = parts.findIndex((p) => p.kind === 'tool' && p.callId === incoming.callId)
  if (index === -1) return [...parts, incoming]
  const existing = parts[index] as ToolPart
  // 결과 이벤트에는 args 가 없다 — 병합이 그 키를 undefined 로 덮으면 호출 인자가 사라진다.
  // (exactOptionalPropertyTypes 가 이 실수를 컴파일 단계에서 잡아 준다.)
  const merged: ToolPart = {
    ...existing,
    kind: 'tool',
    callId: incoming.callId,
    name: incoming.name,
    label: incoming.label,
    phase: incoming.phase,
    ...(incoming.args ? { args: incoming.args } : {}),
    ...(incoming.content !== undefined ? { content: incoming.content } : {}),
  }
  return [...parts.slice(0, index), merged, ...parts.slice(index + 1)]
}

export function applyRunEvent(state: RunState, event: RunEvent): RunState {
  const data = event.data ?? {}

  switch (event.type) {
    case 'thread': {
      // thread 는 최초 1회만 — 재개 턴에서 다시 와도 키를 바꾸지 않는다(이력이 갈린다).
      const threadId = str(data.thread_id) || null
      if (!threadId || state.threadId) return { ...state, status: 'streaming' }
      return { ...state, threadId, status: 'streaming' }
    }

    case 'text': {
      const delta = str(data.delta)
      if (!delta) return state
      return {
        ...state,
        status: 'streaming',
        messages: withAssistant(state.messages, (parts) => appendText(parts, delta)),
      }
    }

    case 'tool': {
      const name = str(data.name, 'tool')
      const phase = data.phase === 'result' ? 'result' : 'call'
      // 호출은 `id`, 결과는 `tool_call_id` 로 온다(백엔드 계약) — 둘 다 없으면 이름으로 묶는다.
      const callId = str(data.id) || str(data.tool_call_id) || `${name}-${state.messages.length}`
      const base: ToolPart = { kind: 'tool', callId, name, label: labelFor(name), phase }
      const args = record(data.args) // 한 번만 호출해야 좁힘이 유지된다(재호출은 다시 넓어진다)
      const part: ToolPart =
        phase === 'call' ? { ...base, ...(args ? { args } : {}) } : { ...base, content: str(data.content) }
      return {
        ...state,
        status: 'streaming',
        // 결과가 호출 없이 먼저 와도 upsert 라 반쪽 카드가 되지 않는다(orphan 방지 — 재개 턴).
        messages: withAssistant(state.messages, (parts) => upsertTool(parts, part)),
      }
    }

    case 'gate': {
      // 휘발 — parts 에 넣지 않는다(이력에 죽은 버튼을 남기지 않는다).
      return {
        ...state,
        status: 'awaiting_gate', // ⚠ done 이 오지 않는다. '끝'이 아니라 '대기'다.
        gate: {
          gateType: str(data.gate_type, 'unknown'),
          question: str(data.question),
          order: record(data.order) ?? null,
          threadId: str(data.thread_id) || state.threadId,
        },
      }
    }

    case 'error': {
      // 백엔드가 이미 마스킹한 문구다(내부 식별자 없음 + 상관 ID). 그대로 보여준다.
      return { ...state, status: 'error', error: str(data.message, '알 수 없는 오류') }
    }

    case 'done':
      return { ...state, status: 'done' }

    case 'ping':
      // 살아 있다는 신호. 진행으로 바꾸지 않고 카운트만 올린다(화면이 "진행 중"을 유지할 근거).
      return { ...state, pings: state.pings + 1 }

    default:
      // 미지의 이벤트 — 무시한다. 여기서 throw 하면 백엔드가 이벤트를 추가하는 순간 화면이 멈춘다.
      return state
  }
}

/** 사용자 발화를 이력에 넣는다(전송 직전). 서버 이벤트가 아니라 로컬 조작이라 별도 함수다. */
export function appendUserMessage(state: RunState, text: string): RunState {
  const message: ChatMessage = { role: 'user', id: `u-${state.messages.length}`, text }
  return {
    ...state,
    messages: [...state.messages, message],
    status: 'streaming',
    error: null,
    gate: null,
  }
}

/** 게이트 응답을 보낸 뒤 — 버튼을 즉시 걷는다(중복 클릭 방지). 결과는 다음 스트림이 말한다. */
export function clearGate(state: RunState): RunState {
  return { ...state, gate: null, status: 'streaming' }
}
